import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, Subscription } from 'rxjs';
import { API_ENDPOINTS } from '../../shared/constants/apiEnpoints';
import { ChartConfig } from '../../shared/chart/chart.component';
import { Toast, ToastComponent } from '../../shared/toast/toast.component';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { computeFitSettings } from '../../shared/export-to-pdf/pdfHelpers';
const vfs = (pdfFonts as any).pdfMake?.vfs ?? (pdfFonts as any).vfs;
(pdfMake as any).vfs = vfs;

interface PracticeOption {
  id: string;
  name: string;
  pmsId?: string;
}

interface EndpointOption {
  pmsObjectId: string;
  endpoint: string;
  pmsObjectIds: string[];
  displayName: string;
}

@Component({
  selector: 'app-daily-load-volume-trend',
  templateUrl: './daily-load-volume-trend.component.html',
  styleUrl: './daily-load-volume-trend.component.scss',
})
export class DailyLoadVolumeTrendComponent implements OnInit, OnDestroy {
  @ViewChild(ToastComponent) toastComponent!: ToastComponent;
  @ViewChild('trendChart') trendChart: any;

  practiceOptions: PracticeOption[] = [];
  selectedPracticeIds: string[] = [];
  tempSelectedPracticeIds: string[] = [];
  allPracticesSelected = true;
  tempAllPracticesSelected = true;
  isPracticeDropdownOpen = false;
  practiceSearchQuery = '';

  endpointOptions: EndpointOption[] = [];
  selectedEndpointIds: string[] = [];
  tempSelectedEndpointIds: string[] = [];
  allEndpointsSelected = true;
  tempAllEndpointsSelected = true;
  isEndpointDropdownOpen = false;
  isEndpointLoading = false;
  endpointSearchQuery = '';
  isExportDropdownOpen = false;

  showDayOverDayChange = true;
  private lastRows: any[] = [];
  tableData: any[] = [];
  isLoading = true;
  showErrorCard = false;
  errorTitle = '';
  errorMessage = '';
  chartData: any[][] = [['Load Date', 'Records Extracted']];
  totalRecords = 0;
  totalDays = 0;

  chartConfig: ChartConfig = {
    title: '',
    type: 'Line',
    is3D: false,
    legend: { position: 'top' },
    height: 420,
    colors: ['#4f46e5'],
    vAxis: { title: 'Records Extracted' },
    hAxis: { title: 'Load Date' },
  };

  private activeRequest?: Subscription;

  constructor(private http: HttpClient, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.fetchPracticeOptions();
  }

  ngOnDestroy(): void {
    this.activeRequest?.unsubscribe();
  }

  togglePracticeDropdown(): void {
    this.isPracticeDropdownOpen = !this.isPracticeDropdownOpen;
  }

  closePracticeDropdown(): void {
    this.isPracticeDropdownOpen = false;
    this.practiceSearchQuery = '';
  }

  applyPracticeFilter(): void {
    const practicesChanged = 
      this.allPracticesSelected !== this.tempAllPracticesSelected ||
      JSON.stringify(this.selectedPracticeIds.sort()) !== JSON.stringify(this.tempSelectedPracticeIds.sort());
    
    this.allPracticesSelected = this.tempAllPracticesSelected;
    this.selectedPracticeIds = [...this.tempSelectedPracticeIds];
    
    if (practicesChanged) {
      this.selectedEndpointIds = [];
      this.tempSelectedEndpointIds = [];
      this.allEndpointsSelected = true;
      this.tempAllEndpointsSelected = true;
      this.endpointOptions = [];
      this.fetchEndpointOptions();
      this.fetchTrendData();
    }
    
    this.closePracticeDropdown();
  }

  toggleEndpointDropdown(): void {
    this.isEndpointDropdownOpen = !this.isEndpointDropdownOpen;
  }

  closeEndpointDropdown(): void {
    this.isEndpointDropdownOpen = false;
    this.endpointSearchQuery = '';
  }

  applyEndpointFilter(): void {
    // Apply temp selections to actual selections
    const endpointsChanged = 
      this.allEndpointsSelected !== this.tempAllEndpointsSelected ||
      JSON.stringify(this.selectedEndpointIds.sort()) !== JSON.stringify(this.tempSelectedEndpointIds.sort());
    
    this.allEndpointsSelected = this.tempAllEndpointsSelected;
    this.selectedEndpointIds = [...this.tempSelectedEndpointIds];
    
    if (endpointsChanged) {
      this.fetchTrendData();
    }
    
    this.closeEndpointDropdown();
  }

  onAllEndpointsToggle(checked: boolean): void {
    this.tempAllEndpointsSelected = checked;
    this.tempSelectedEndpointIds = [];
  }

  onEndpointToggle(endpoint: string, checked: boolean): void {
    if (this.tempAllEndpointsSelected) {
      this.tempAllEndpointsSelected = false;
      this.tempSelectedEndpointIds = checked ? [endpoint] : [];
      return;
    }
    const next = new Set(this.tempSelectedEndpointIds);
    checked ? next.add(endpoint) : next.delete(endpoint);
    if (next.size === this.endpointOptions.length) {
      this.tempAllEndpointsSelected = true;
      this.tempSelectedEndpointIds = [];
    } else {
      this.tempAllEndpointsSelected = false;
      this.tempSelectedEndpointIds = Array.from(next);
    }
  }

  get filteredEndpointOptions(): EndpointOption[] {
    const q = this.endpointSearchQuery.trim().toLowerCase();
    return q ? this.endpointOptions.filter((e) => e.displayName.toLowerCase().includes(q)) : this.endpointOptions;
  }

  isEndpointSelected(endpoint: string): boolean {
    return this.tempSelectedEndpointIds.includes(endpoint);
  }

  private toTitleCase(s: string): string {
    return s.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  }

  onAllPracticesToggle(checked: boolean): void {
    this.tempAllPracticesSelected = checked;
    this.tempSelectedPracticeIds = [];
  }

  onPracticeToggle(practiceId: string, checked: boolean): void {
    if (this.tempAllPracticesSelected) {
      this.tempAllPracticesSelected = false;
      this.tempSelectedPracticeIds = checked ? [practiceId] : [];
      return;
    }
    const next = new Set(this.tempSelectedPracticeIds);
    checked ? next.add(practiceId) : next.delete(practiceId);
    if (next.size === this.practiceOptions.length) {
      this.tempAllPracticesSelected = true;
      this.tempSelectedPracticeIds = [];
    } else {
      this.tempAllPracticesSelected = false;
      this.tempSelectedPracticeIds = Array.from(next);
    }
  }

  get filteredPracticeOptions(): PracticeOption[] {
    const q = this.practiceSearchQuery.trim().toLowerCase();
    return q ? this.practiceOptions.filter((p) => p.name.toLowerCase().includes(q)) : this.practiceOptions;
  }

  isPracticeSelected(practiceId: string): boolean {
    return this.tempSelectedPracticeIds.includes(practiceId);
  }

  get selectedPmsIds(): string[] {
    if (this.allPracticesSelected) {
      return [...new Set(
        this.practiceOptions
          .map(p => p.pmsId)
          .filter((pmsId): pmsId is string => !!pmsId)
      )];
    }
    if (this.selectedPracticeIds.length === 0) return [];
    return [...new Set(
      this.selectedPracticeIds
        .map(id => this.practiceOptions.find(p => p.id === id)?.pmsId)
        .filter((pmsId): pmsId is string => !!pmsId)
    )];
  }

  get hasActiveFilters(): boolean {
    return !this.allPracticesSelected || !this.allEndpointsSelected;
  }

  applyAllFilters(): void {
    const practicesChanged =
      this.allPracticesSelected !== this.tempAllPracticesSelected ||
      JSON.stringify([...this.selectedPracticeIds].sort()) !== JSON.stringify([...this.tempSelectedPracticeIds].sort());
    const endpointsChanged =
      this.allEndpointsSelected !== this.tempAllEndpointsSelected ||
      JSON.stringify([...this.selectedEndpointIds].sort()) !== JSON.stringify([...this.tempSelectedEndpointIds].sort());

    this.allPracticesSelected = this.tempAllPracticesSelected;
    this.selectedPracticeIds = [...this.tempSelectedPracticeIds];
    this.allEndpointsSelected = this.tempAllEndpointsSelected;
    this.selectedEndpointIds = [...this.tempSelectedEndpointIds];

    this.isPracticeDropdownOpen = false;
    this.isEndpointDropdownOpen = false;
    this.practiceSearchQuery = '';
    this.endpointSearchQuery = '';

    if (practicesChanged) {
      this.endpointOptions = [];
      this.fetchEndpointOptions();
    }
    if (practicesChanged || endpointsChanged) {
      this.fetchTrendData();
    }
  }

  refreshTrend(): void {
    this.fetchTrendData();
  }

  toggleDayOverDay(): void {
    this.showDayOverDayChange = !this.showDayOverDayChange;
    this.updateChart(this.lastRows);
    this.cdr.detectChanges();
    setTimeout(() => this.trendChart?.drawChart());
  }

  resetFilters(): void {
    this.allPracticesSelected = true;
    this.tempAllPracticesSelected = true;
    this.selectedPracticeIds = [];
    this.tempSelectedPracticeIds = [];
    this.allEndpointsSelected = true;
    this.tempAllEndpointsSelected = true;
    this.selectedEndpointIds = [];
    this.tempSelectedEndpointIds = [];
    this.endpointOptions = [];
    this.practiceSearchQuery = '';
    this.endpointSearchQuery = '';
    this.isPracticeDropdownOpen = false;
    this.isEndpointDropdownOpen = false;
    this.fetchEndpointOptions();
    this.fetchTrendData();
  }

  private fetchPracticeOptions(): void {
    this.isLoading = true;
    this.http.get<any>(API_ENDPOINTS.GETALLACTIVEPRACTICES).subscribe({
      next: (response) => {
        this.practiceOptions = (response?.data ?? [])
          .map((p: any) => ({ id: p.id, name: p.pmsName ? `${p.practiceCode} (${p.pmsName})` : p.practiceCode, pmsId: p.pmsId }))
          .filter((p: PracticeOption) => !!p.id)
          .sort((a: PracticeOption, b: PracticeOption) => a.name.localeCompare(b.name));
        this.fetchEndpointOptions();
        this.fetchTrendData();
      },
      error: (error) => this.handleError(error, 'Failed to load practices'),
    });
  }

  private fetchEndpointOptions(): void {
    const pmsIds = this.selectedPmsIds;
    if (pmsIds.length === 0) {
      // If all practices selected but options not loaded yet, wait
      if (this.allPracticesSelected && this.practiceOptions.length === 0) return;
      this.endpointOptions = []; return;
    }
    this.isEndpointLoading = true;

    const requests = pmsIds.map(pmsId =>
      this.http.get<any>(`${API_ENDPOINTS.PMS_OBJECT_ENDPOINT_SOURCE_MAPPING}?pmsId=${pmsId}`)
    );

    forkJoin(requests).subscribe({
      next: (responses: any[]) => {
        // Merge endpoints across all PMS, union by endpoint name, union pmsObjectIds
        const mergedMap = new Map<string, Set<string>>();
        responses.forEach(response => {
          const data: any[] = response?.data ?? [];
          data.forEach(e => {
            if (!mergedMap.has(e.endpoint)) mergedMap.set(e.endpoint, new Set());
            (e.pmsObjectIds ?? []).forEach((id: string) => mergedMap.get(e.endpoint)!.add(id));
          });
        });
        this.endpointOptions = Array.from(mergedMap.entries())
          .map(([endpoint, ids]) => ({
            pmsObjectId: [...ids][0] ?? '',
            pmsObjectIds: [...ids],
            endpoint,
            displayName: this.toTitleCase(endpoint),
          }))
          .sort((a, b) => a.displayName.localeCompare(b.displayName));
        this.isEndpointLoading = false;
        this.cdr.detectChanges();
      },
      error: () => { this.isEndpointLoading = false; this.endpointOptions = []; },
    });
  }

  private fetchTrendData(): void {
    if (!this.allPracticesSelected && this.selectedPracticeIds.length === 0) {
      this.chartData = [['Load Date', 'Records Extracted']];
      this.totalDays = 0;
      this.totalRecords = 0;
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    const practiceLabel = this.allPracticesSelected ? 'All Practices' : this.selectedPracticeNames.join(', ');
    const endpointLabel = this.allEndpointsSelected ? 'All Endpoints' : this.selectedEndpointNames.join(', ') || this.selectedEndpointIds.join(', ');

    this.isLoading = true;
    this.activeRequest?.unsubscribe();

    let params = new HttpParams().set('allPractices', this.allPracticesSelected ? 'true' : 'false');
    if (!this.allPracticesSelected) {
      this.selectedPracticeIds.forEach((id) => { params = params.append('practiceId', id); });
    }
    
    // Add endpoint filters - only if not all endpoints selected
    if (!this.allEndpointsSelected && this.selectedEndpointIds.length > 0) {
      // Collect all pmsObjectIds for selected endpoints
      const selectedPmsObjectIds: string[] = [];
      this.selectedEndpointIds.forEach(endpoint => {
        const endpointOption = this.endpointOptions.find(e => e.endpoint === endpoint);
        if (endpointOption) {
          selectedPmsObjectIds.push(...endpointOption.pmsObjectIds);
        }
      });
      selectedPmsObjectIds.forEach(id => { params = params.append('pmsObjectId', id); });
    }

    this.activeRequest = this.http.get<any>(API_ENDPOINTS.DAILY_LOAD_VOLUME_TREND, { params }).subscribe({
      next: (response) => {
        this.lastRows = Array.isArray(response?.data) ? response.data : [];
        this.updateChart(this.lastRows, practiceLabel, endpointLabel);
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => this.handleError(error, 'Failed to load daily load volume data'),
    });
  }

  private updateChart(rows: any[], practiceLabel?: string, endpointLabel?: string): void {
    const practice = practiceLabel ?? (this.allPracticesSelected ? 'All Practices' : this.selectedPracticeNames.join(', '));
    const endpoint = endpointLabel ?? (this.allEndpointsSelected ? 'All Endpoints' : this.selectedEndpointNames.join(', '));
    const counts = rows.map((row) => Number(row.loadCount ?? 0));
    const labels = rows.map((row) => row.loadDate ? moment(row.loadDate).format('ddd, MMM D') : '');

    const dataRows = counts.map((count, i) => {
      let annotation: string | null = null;
      let dayOverDay = '';
      if (i > 0) {
        const prev = counts[i - 1];
        const diff = count - prev;
        const sign = diff >= 0 ? '+' : '';
        if (prev !== 0) {
          const pct = (diff / prev) * 100;
          const formatted = `${sign}${diff.toLocaleString()} (${sign}${pct.toFixed(3)}%)`;
          dayOverDay = formatted;
          if (this.showDayOverDayChange) annotation = formatted;
        } else {
          const formatted = `${sign}${diff.toLocaleString()}`;
          dayOverDay = formatted;
          if (this.showDayOverDayChange) annotation = formatted;
        }
      }
      return { chartRow: [labels[i], count, annotation], dayOverDay };
    });

    this.chartData = [
      ['Load Date', 'Records Extracted', { role: 'annotation', type: 'string' }],
      ...dataRows.map(r => r.chartRow)
    ];
    this.totalDays = dataRows.length;
    this.totalRecords = counts.reduce((sum, c) => sum + c, 0);

    this.tableData = rows.map((row, i) => ({
      loadDate: labels[i],
      practice,
      endpoint,
      loadCount: counts[i],
    }));

    if (dataRows.length > 0) {
      const max = Math.max(...counts);
      const step = this.niceStep(max);
      const ticks = [];
      for (let t = 0; t <= max + step; t += step) ticks.push(t);
      this.chartConfig = { ...this.chartConfig, vAxis: { title: 'Records Extracted', ticks } };
    }
  }

  private niceStep(max: number): number {
    if (max <= 0) return 1;
    const rough = max / 5;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
    const factor = rough / magnitude;
    const nice = factor < 1.5 ? 1 : factor < 3.5 ? 2 : factor < 7.5 ? 5 : 10;
    return nice * magnitude;
  }

  private handleError(error: any, fallbackMessage: string): void {
    this.showErrorCard = true;
    this.errorTitle = error?.status === 403 ? 'Forbidden' : error?.status === 404 ? 'Not Found' : error?.status >= 500 ? 'Internal Server Error' : 'Error';
    this.errorMessage = error?.error?.errorMessage || error?.error?.detailedErrorMessage || fallbackMessage;
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  async exportTableData(): Promise<void> {
    if (!this.tableData.length) return;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Daily Load Volume');
    worksheet.addRow(['Load Date', 'Practice', 'Endpoint', 'Records Extracted']);
    this.tableData.forEach(row => worksheet.addRow([row.loadDate, row.practice, row.endpoint, row.loadCount]));
    worksheet.columns.forEach(col => { col.width = 25; });
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `Daily_Load_Volume_${moment().format('YYYY-MM-DD-HH-mm-ss')}.xlsx`);
  }

  exportToPDF(): void {
    if (!this.tableData.length) return;

    const title = 'Daily Load Volume Trend';
    const subtitle = `${this.practiceBadgeLabel}   |   ${this.endpointBadgeLabel}`;
    const colHeaders = ['LOAD DATE', 'PRACTICE', 'ENDPOINT', 'RECORDS EXTRACTED'];
    const fields = ['loadDate', 'practice', 'endpoint', 'loadCount'];
    const margins: [number, number, number, number] = [40, 60, 40, 40];
    const targetPadding = 2.5;
    const lineHeight = 0.9;

    const { fontSize, pageSize } = computeFitSettings(colHeaders, fields, this.tableData, 'A4', 'landscape', margins, 10, 8, targetPadding);

    const headerRow = colHeaders.map(text => ({
      text, bold: true, color: '#0D47A1', fillColor: '#E3F2FD',
      margin: [0, targetPadding, 0, targetPadding], noWrap: true
    }));

    const bodyRows = this.tableData.map(row => [
      { text: row.loadDate, margin: [0, targetPadding, 0, targetPadding], lineHeight, noWrap: true },
      { text: row.practice, margin: [0, targetPadding, 0, targetPadding], lineHeight },
      { text: row.endpoint, margin: [0, targetPadding, 0, targetPadding], lineHeight },
      { text: row.loadCount.toLocaleString(), alignment: 'right', margin: [0, targetPadding, 0, targetPadding], lineHeight, noWrap: true },
    ]);

    const now = new Date();
    const generatedAt = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

    const docDef: any = {
      info: { title, subject: `${title} (${generatedAt})` },
      pageSize,
      pageOrientation: 'landscape',
      pageMargins: margins,
      defaultStyle: { fontSize, lineHeight },
      watermark: { text: 'Dentalcorp', color: '#777', opacity: 0.12, angle: 35, bold: true },
      header: (currentPage: number, pageCount: number) => ({
        columns: [
          { text: title, style: 'headerLeft', color: '#1976D2' },
          { text: `Page ${currentPage} of ${pageCount}`, alignment: 'right', style: 'headerRight', color: '#555' }
        ],
        margin: [40, 20, 40, 0]
      }),
      footer: () => ({
        columns: [
          { text: `Generated: ${generatedAt}`, alignment: 'left', color: '#444' },
          { text: `Days: ${this.totalDays}   |   Total: ${this.totalRecordsFormatted} records`, alignment: 'right', color: '#444' }
        ],
        margin: [40, 10, 40, 0],
        fontSize: 9
      }),
      content: [
        { text: title, style: 'title', color: '#0D47A1', margin: [0, 0, 0, 4] },
        { text: subtitle, fontSize: 9, color: '#555', margin: [0, 0, 0, 8] },
        {
          table: { headerRows: 1, widths: ['auto', '*', '*', 'auto'], body: [headerRow, ...bodyRows] },
          layout: {
            paddingLeft: () => targetPadding, paddingRight: () => targetPadding,
            paddingTop: () => targetPadding, paddingBottom: () => targetPadding,
            hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? 0.8 : 0.4,
            vLineWidth: () => 0.4,
            hLineColor: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? '#B0BEC5' : '#CFD8DC',
            vLineColor: () => '#CFD8DC'
          }
        }
      ],
      styles: {
        title: { fontSize: Math.max(12, fontSize + 2), bold: true },
        headerLeft: { fontSize: 9 },
        headerRight: { fontSize: 9 }
      }
    };

    pdfMake.createPdf(docDef).download(`Daily_Load_Volume_${moment().format('YYYY-MM-DD-HH-mm-ss')}.pdf`);
  }

  closeErrorCard(): void {
    this.showErrorCard = false;
    this.cdr.detectChanges();
  }

  get selectedEndpointNames(): string[] {
    return this.selectedEndpointIds
      .map(endpoint => this.endpointOptions.find(e => e.endpoint === endpoint)?.displayName)
      .filter((name): name is string => !!name);
  }

  get selectedPracticeNames(): string[] {
    return this.practiceOptions.filter((p) => this.selectedPracticeIds.includes(p.id)).map((p) => p.name);
  }

  get practiceBadgeLabel(): string {
    if (this.allPracticesSelected) return 'Practices: All';
    if (this.selectedPracticeNames.length === 0) return 'Practices: None';
    const extra = this.selectedPracticeNames.length - 1;
    return extra > 0
      ? `Practices: ${this.selectedPracticeNames[0]} +${extra}`
      : `Practices: ${this.selectedPracticeNames[0]}`;
  }

  get endpointBadgeLabel(): string {
    if (this.allEndpointsSelected) return 'Endpoints: All';
    if (this.selectedEndpointNames.length === 0) return 'Endpoints: None';
    const extra = this.selectedEndpointNames.length - 1;
    return extra > 0
      ? `Endpoints: ${this.selectedEndpointNames[0]} +${extra}`
      : `Endpoints: ${this.selectedEndpointNames[0]}`;
  }

  get totalRecordsFormatted(): string {
    const v = this.totalRecords;
    if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (v >= 1_000) return (v / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    return v.toString();
  }
}
